import { INTEREST_TOPICS } from './interests';
import { SOCIAL_TOPICS, type SocialPost, type SocialProfile } from './types';

export const EXPLORE_VIEWS = ['trending', 'latest', 'people', 'media'] as const;
export type ExploreView = typeof EXPLORE_VIEWS[number];
export type ExploreSelection = { category: string; tag: string; view: ExploreView };
const descriptions: Record<string, string> = {
  design: 'Interfaces, objects & identity', technology: 'Code, data & new ideas',
  photography: 'Life through a lens', art: 'Made by hand & imagination',
  music: 'Sounds worth sharing', writing: 'Stories, words & perspectives',
  building: 'Projects taking shape', travel: 'Places, nature & culture',
  food: 'From kitchen to table', fitness: 'Movement & wellbeing',
};
export const EXPLORE_CATEGORIES = SOCIAL_TOPICS.map(label => ({ id: label.toLowerCase(), label, description: descriptions[label.toLowerCase()] }));

// Match exact terms, including equivalent multiword hashtags, never substrings.
export function exploreTerm(value: string) {
  return value.normalize('NFKC').toLowerCase().trim().replace(/^#/, '').replace(/[\s_-]+/g, '');
}
export function exploreSelection(params: { get(name: string): string | null }): ExploreSelection {
  const category = (params.get('category') || '').toLowerCase();
  const tag = (params.get('tag') || '').normalize('NFKC').trim().replace(/^#/, '').toLowerCase().slice(0, 32);
  const legacyCategory = !category ? EXPLORE_CATEGORIES.find(item => item.id === tag)?.id : undefined;
  const view = params.get('view');
  return {
    category: EXPLORE_CATEGORIES.some(item => item.id === category) ? category : legacyCategory || '',
    tag: legacyCategory ? '' : tag,
    view: EXPLORE_VIEWS.includes(view as ExploreView) ? view as ExploreView : 'trending',
  };
}
export function explorePath(selection: ExploreSelection) {
  const query = new URLSearchParams();
  if (selection.category) query.set('category', selection.category);
  if (selection.tag) query.set('tag', selection.tag);
  if (selection.view !== 'trending') query.set('view', selection.view);
  return `/explore${query.size ? `?${query}` : ''}`;
}
export function exploreLabel(selection: ExploreSelection) {
  return selection.tag ? `#${selection.tag}` : EXPLORE_CATEGORIES.find(item => item.id === selection.category)?.label || 'All categories';
}
export function matchesExplore(terms: readonly string[], selection: ExploreSelection) {
  const normalized = new Set(terms.map(exploreTerm));
  if (selection.tag && !normalized.has(exploreTerm(selection.tag))) return false;
  if (!selection.category) return true;
  const categoryTerms = [selection.category, ...INTEREST_TOPICS.filter(topic => topic.parent === selection.category).map(topic => topic.id)];
  return categoryTerms.some(term => normalized.has(exploreTerm(term)));
}
export function exploreTrendingScore(post: SocialPost, anchor: number) {
  const ageHours = Math.max(0, (anchor - Date.parse(post.createdAt)) / 3600000);
  const engagement = Math.log1p(Math.max(0, post.likes)) + 1.5 * Math.log1p(Math.max(0, post.comments))
    + Math.log1p(Math.max(0, post.reposts || 0)) + 0.5 * Math.log1p(Math.max(0, post.shares || 0));
  return (1 + engagement) / Math.pow(1 + ageHours / 24, 1.25);
}
export type ExploreResult = { posts: SocialPost[]; profiles: SocialProfile[]; topics?: Array<{ tag: string; label: string; posts: number }>; nextCursor: string | null };
